import { useTranslation } from '@/i18n'
import { cn } from '@/lib/utils'
import { isManualMatch, toPercent, toneFor, type MatchTone } from './matchTone'

/**
 * The confidence pill next to a matched tag — "98% Matching" — or the
 * "Manual Approval" pill when a person made the connection themselves, in
 * which case a percentage would be meaningless (it is always 1.0 by
 * construction).
 *
 * Tints reuse the emerald/amber/rose set the Match Entity panel already uses
 * rather than introducing a fourth palette for the same concept. IntuiBadge is
 * deliberately not used: it has `success` and `warning` but no soft-red
 * variant, and `destructive` is a solid fill that reads as an error rather
 * than a low score.
 */

const TONE_CLASS: Record<MatchTone, string> = {
  strong: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  weak: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
}

interface Props {
  confidence: number
  /** `'manual'` renders the Manual Approval pill instead of a percentage. */
  matchMethod?: string | null
  className?: string
}

const MatchBadge = ({ confidence, matchMethod, className }: Props) => {
  const { t } = useTranslation()
  const manual = isManualMatch(matchMethod)

  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
        TONE_CLASS[manual ? 'strong' : toneFor(confidence)],
        className,
      )}
    >
      {manual
        ? t('data_catalog:tag_mapping.badge.manual_approval', { defaultValue: 'Manual approval' })
        : t('data_catalog:tag_mapping.badge.matching', {
            // The number alone, as the design prints it — "98% Matching" in a
            // column already headed Confidence said the same word twice.
            defaultValue: '{{percent}}%',
            percent: toPercent(confidence),
          })}
    </span>
  )
}

export default MatchBadge
